import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useSeo, SERVICE_LEVELS } from "@/lib/site";
import { Reveal, MaskLines, ChapterHeading } from "@/components/Reveal";

const DETAILS = {
  "supply-install": ["Site measure and written quote", "Blocks, mortar, reinforcement supplied", "Footings and piers built to engineer's spec", "Core-fill and clean-down included", "Most walls done in 1–3 days on site"],
  "supply-only": ["Palletised delivery to site", "Crane truck or forklift drop", "10–15% extra for cuts included in quote", "Delivery within 5–10 business days on stocked lines", "Laying guide for your builder"],
  "install-only": ["We install blocks you've already bought", "Footings and reinforcement by us", "Honest advice if your blocks aren't suitable", "Great for owner-builders and landscapers"],
};

export default function Services() {
  useSeo("Services — Supply, Install & Both | Breeze Craft Sydney", "Breeze block supply only, supply + install, or install only across Sydney. Compare options and get a quote.");
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 sm:py-20" data-testid="services-page">
      <p className="kicker mb-4">Services</p>
      <h1 className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-ink" data-testid="services-heading">
        <MaskLines lines={["Supply it, lay it,", "or both."]} />
      </h1>
      <Reveal delay={0.2} className="mt-5 max-w-2xl">
        <p className="text-neutral-600 leading-relaxed">
          Every job is quoted individually — block style, wall size, footings, reinforcement and access all change the number. Here's how each way of working with us runs.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10" data-testid="services-grid">
        {SERVICE_LEVELS.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08} className="bg-bone p-8 flex flex-col">
            <p className="font-mono text-xs text-terracotta tracking-[0.25em]">0{i + 1}</p>
            <h2 className="mt-3 font-display text-2xl font-medium text-ink" data-testid={`service-title-${s.id}`}>{s.label}</h2>
            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{s.blurb}</p>
            <ul className="mt-6 flex-1 space-y-2.5">
              {DETAILS[s.id].map((d) => (
                <li key={d} className="flex gap-2.5 text-sm text-neutral-700">
                  <Check size={15} className="text-terracotta shrink-0 mt-0.5" /> {d}
                </li>
              ))}
            </ul>
            <Link
              to={`/quote?service=${s.id}`}
              data-testid={`service-quote-${s.id}`}
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-terracotta hover:text-terracotta-dark transition-colors"
            >
              Quote me on this <ArrowRight size={15} />
            </Link>
          </Reveal>
        ))}
      </div>

      <section className="mt-24">
        <ChapterHeading number="How it runs" title="From quote to finished wall" />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-10">
          {[
            ["01", "You send the form", "Photos, rough size, suburb. Five minutes."],
            ["02", "We call within a day", "Talk through the job, then a site measure if needed."],
            ["03", "Written fixed quote", "Blocks, labour, footings, delivery — one number."],
            ["04", "We build it", "Stocked lines installed within 2–4 weeks of go-ahead."],
          ].map(([n, t, d], i) => (
            <Reveal key={n} delay={i * 0.07}>
              <p className="font-mono text-xs text-terracotta tracking-[0.25em]">{n}</p>
              <p className="mt-2 font-display text-xl font-medium text-ink">{t}</p>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
